import { createFileRoute } from '@tanstack/react-router'
import Pricings from '@/components/Pricings'
// import { ScrollPromoGrowthDialog } from '@/components/providers/scroll-promo-growth-dialog'
import { createLLMOMeta, createWebPageSchema, createBreadcrumbSchema } from '@/lib/seo'
import { getUserGeoData, type GeoData } from '@/lib/geo.server'
import { CURRENCY_PRICING, type SupportedCurrency } from '@/lib/currency-utils'

// Helper to get price for schema based on currency
function getSchemaPrice(plan: 'starter' | 'professional', currency: SupportedCurrency) {
  const pricing = CURRENCY_PRICING[currency][plan]
  return {
    monthly: pricing.monthly.toString(),
    yearly: pricing.yearly.toString(),
    currency: currency.toUpperCase(),
  }
}

export const Route = createFileRoute('/pricing')({
  loader: async () => {
    const geoData = await getUserGeoData()
    return { geoData }
  },
  head: ({ loaderData }) => {
    const geoData = loaderData?.geoData as GeoData | undefined
    const currency = geoData?.currency || 'usd'

    // Get pricing for schema.org structured data
    const starterPricing = getSchemaPrice('starter', currency)
    const proPricing = getSchemaPrice('professional', currency)

    return {
      title: 'Pricing - Zapify | AI-Powered Instagram Automation',
      meta: [
        ...createLLMOMeta({
          title: 'Pricing - Zapify | AI-Powered Instagram Automation',
          description: 'Flexible pricing for every creator — switch anytime and save more on yearly billing. Start automating your Instagram comments with AI today.',
          url: 'https://zapify.pro/pricing',
        }),
        { property: "og:image", content: "https://cms.zapify.pro/uploads/OG_image1_f82f5871e2.png" },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:locale", content: "en_US" },
      ],
      links: [
        { rel: "canonical", href: "https://zapify.pro/pricing" }
      ],
      scripts: [
        createWebPageSchema({
          name: 'Pricing - Zapify',
          description: 'Flexible pricing for every creator — switch anytime and save more on yearly billing',
          url: 'https://zapify.pro/pricing',
        }),
        createBreadcrumbSchema({
          itemListElement: [
            {
              position: 1,
              name: 'Home',
              item: 'https://zapify.pro',
            },
            {
              position: 2,
              name: 'Pricing',
              item: 'https://zapify.pro/pricing',
            },
          ],
        }),
        {
          type: 'application/ld+json' as const,
          children: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'SoftwareApplication',
            name: 'Zapify',
            applicationCategory: 'BusinessApplication',
            offers: [
              {
                '@type': 'Offer',
                name: 'Free Trial',
                price: '0',
                priceCurrency: starterPricing.currency,
              },
              {
                '@type': 'Offer',
                name: 'Starter',
                price: starterPricing.monthly,
                priceCurrency: starterPricing.currency,
                priceSpecification: {
                  '@type': 'UnitPriceSpecification',
                  price: starterPricing.monthly,
                  priceCurrency: starterPricing.currency,
                  unitText: 'MONTH',
                },
              },
              {
                '@type': 'Offer',
                name: 'Professional',
                price: proPricing.monthly,
                priceCurrency: proPricing.currency,
                priceSpecification: {
                  '@type': 'UnitPriceSpecification',
                  price: proPricing.monthly,
                  priceCurrency: proPricing.currency,
                  unitText: 'MONTH',
                },
              },
            ],
          }),
        },
      ],
    }
  },
  component: PricingPage
})

function PricingPage() {
  const { geoData } = Route.useLoaderData()

  return (
    <>
      <main className="relative z-10 w-full min-w-0 bg-background pt-24 sm:pt-28">
        <Pricings countryCode={geoData.countryCode} currency={geoData.currency} />
      </main>
      {/* <ScrollPromoGrowthDialog /> */}
    </>
  )
}
